import useFocusTrap from '@charlietango/use-focus-trap';
import React, { FC, ReactNode, useCallback, useLayoutEffect } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { CONTENT_MAX_WIDTH_PX } from '#/service/constants';
import { falsyToNull, noop, remify } from '#/service/utils';
import { modalActions } from '#/store/modal/modal.store';

import { ReactPortal } from '../Portal';
import {
  BackContainer,
  BackImage,
  ModalBody,
  StyledOverlay,
} from './FullPageModal.styled';

export const FULL_PAGE_MODAL_ID = 'fullPageModal';

type TextAlign = 'left' | 'center' | 'right';

interface CenteredContentProps {
  /**
   * @optional Gap between children. Please note that providing value here
   * will disable margins on direct children
   */
  $gap?: string | 0;
  $maxWidth?: number;
}

interface TitleContainerProps extends CenteredContentProps {
  children?: ReactNode;
  $textAlign?: TextAlign;
  $bottomMargin?: number;
  $isFullWidth?: boolean;
}

interface TitlesProps {
  title: string;
  subtitle?: string;
  textAlign?: TextAlign;
  gap?: number;
  titleFont?: string;
  subtitleFont?: string;
  bottomMargin?: number;
  isFullWidth?: boolean;
}

interface Props {
  children?: ReactNode;
  /**
   * @optional Background image URL.
   * If not provided, the default (`var(--pageBackground)`) background color will be used
   * */
  backgroundImageUrl?: string;

  /** @default false If `true`, the modal will close when the `Esc` key is pressed */
  closeOnEsc?: boolean;

  /**
   * @default true Applies a statically defined linear gradient on the background image
   * Disable this if you want to use background image with a gradient already applied
   * */
  applyDefaultGradient?: boolean;

  /** @optional Handler that will run whenever modal closes */
  onClose?: () => void;
}

interface FullPageModalComponent extends FC<Props> {
  Titles: FC<TitlesProps>;
  Title: FC<{ children?: ReactNode; $font?: string }>;
  Subtitle: FC<{ children?: ReactNode; $font?: string }>;
  TitleContainer: FC<TitleContainerProps>;
  CenteredContent: FC<CenteredContentProps | Props>;
}

/**
 * This is a full page modal. You can use it when you have a design that hides all other parts of the application (menu, footer, etc.)
 * It's not a perfect solution, but for now it works fine. At some point, we should replace it with something more convenient and accessibility-friendly
 * This is a compound component. You can use it like this:
 * @example
 * <FullPageModal backgroundImageUrl={backgroundImageUrl}>
     <FullPageModal.CenteredContent>
       <FullPageModal.Title>Lovely title</FullPageModal.Title>
       ...
     </FullPageModal.CenteredContent>
    </FullPageModal>
 */
export const FullPageModal: FullPageModalComponent = ({
  children,
  backgroundImageUrl,
  applyDefaultGradient = true,
  onClose = noop,
  closeOnEsc = false,
  ...rest
}) => {
  const theme = useTheme();
  // by default, useFocusTrap would automatically focus on the first focusable element,
  // which would also scroll it into view. the only way to prevent this is to pass a
  // selector that won't find anything. the user will still not be able to focus outside.
  const ref = useFocusTrap(true, { focusSelector: '#non-existent-id' });

  useLayoutEffect(() => {
    // We have to actually remove the body scrollbar here for aesthetic reasons
    document.body.style.overflow = 'hidden';

    // Notify that full page modal is open
    modalActions.setFullPageModalOpen(true);

    return () => {
      document.body.style.overflow = 'auto';
      // Notify that full page modal is closed
      modalActions.setFullPageModalOpen(false);
    };
  }, []);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && closeOnEsc && onClose) {
        ev.stopPropagation();
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  const customImage =
    falsyToNull(backgroundImageUrl) ?? theme?.pageBackgroundImage?.image;

  return (
    <ReactPortal>
      <StyledOverlay
        data-nosnippet
        data-testid="fullPageModal-backdrop"
        id={FULL_PAGE_MODAL_ID}
        onKeyDown={onKeyDown}
        ref={ref}
      >
        {customImage && (
          <BackContainer>
            <BackImage
              data-apply-gradient={applyDefaultGradient}
              data-testid="fullPageModal-backImage"
              src={customImage}
            />
          </BackContainer>
        )}
        <ModalBody
          aria-labelledby="fullPageModal-title"
          aria-modal={true}
          data-testid="fullPageModal-body"
          {...rest}
        >
          {children}
        </ModalBody>
      </StyledOverlay>
    </ReactPortal>
  );
};

const Titles = ({
  title,
  subtitle,
  textAlign,
  gap,
  titleFont,
  subtitleFont,
  bottomMargin,
  isFullWidth,
}: TitlesProps) => {
  return (
    <FullPageModal.TitleContainer
      $bottomMargin={bottomMargin}
      $gap={gap ? remify(gap) : remify(8)}
      $isFullWidth={isFullWidth}
      $textAlign={textAlign}
    >
      <FullPageModal.Title $font={titleFont}>{title}</FullPageModal.Title>
      {subtitle ? (
        <FullPageModal.Subtitle $font={subtitleFont}>
          {subtitle}
        </FullPageModal.Subtitle>
      ) : null}
    </FullPageModal.TitleContainer>
  );
};

FullPageModal.Titles = Titles;

FullPageModal.TitleContainer = styled.div<TitleContainerProps>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ $bottomMargin }) =>
    $bottomMargin ? remify($bottomMargin) : remify(48)};
  text-align: ${({ $textAlign }) => $textAlign ?? 'center'};
  max-width: ${({ $isFullWidth }) =>
    $isFullWidth ? '100%' : remify(CONTENT_MAX_WIDTH_PX)};
  gap: ${({ $gap }) => $gap ?? 0};
`;

FullPageModal.Title = styled.h1.attrs(() => ({
  id: 'fullPageModal-title',
}))<{ $font?: string }>`
  font: ${({ $font }) => $font ?? 'var(--fontStyleTitle1)'};
  color: var(--pageHeadingForeground);
  width: 100%;
  margin: 0;
`;

FullPageModal.Subtitle = styled.p<{ $font?: string }>`
  font: ${({ $font }) => $font ?? 'var(--fontStyleSubtitle)'};
  color: var(--pageSubtitleForeground);
  width: 100%;
  margin: 0;
`;

FullPageModal.CenteredContent = styled.div<CenteredContentProps>`
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${({ $maxWidth }) => ($maxWidth ? remify($maxWidth) : '100%')};
  padding: 1rem;

  /* When $maxWidth is set, take full width so the column fills up to max-width; otherwise shrink to content */
  width: ${({ $maxWidth }) => ($maxWidth ? '100%' : 'auto')};

  ${({ $gap }) =>
    $gap &&
    css`
      gap: ${$gap};

      > * {
        margin: 0;
      }
    `}
`;
